// src/pages/CompanyManagement/tabs/AdvancedTab.jsx
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Chip,
  IconButton,
  Grid
} from '@mui/material';
import {
  Security as SecurityIcon,
  Microsoft as MicrosoftIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

const AdvancedTab = ({ company, onChange, t }) => {
  const settings = company.settings || {};
  const [newDomain, setNewDomain] = useState('');
  const [newRole, setNewRole] = useState('');

  const microsoftTenantId = settings.microsoftTenantId || '';
  const allowedDomains = settings.allowedDomains || [];
  const allowedRoles = settings.allowedRoles || ['admin', 'manager', 'user'];

  const handleTenantIdChange = (value) => {
    onChange({
      settings: {
        ...settings,
        microsoftTenantId: value
      }
    });
  };

  const handleActiveToggle = (checked) => {
    onChange({
      active: checked
    });
  };

  const handleAddDomain = () => {
    if (!newDomain || allowedDomains.includes(newDomain)) {
      return;
    }

    onChange({
      settings: {
        ...settings,
        allowedDomains: [...allowedDomains, newDomain]
      }
    });

    setNewDomain('');
  };

  const handleRemoveDomain = (domain) => {
    onChange({
      settings: {
        ...settings,
        allowedDomains: allowedDomains.filter(d => d !== domain)
      }
    });
  };

  const handleAddRole = () => {
    if (!newRole || allowedRoles.includes(newRole)) {
      return;
    }

    onChange({
      settings: {
        ...settings,
        allowedRoles: [...allowedRoles, newRole]
      }
    });

    setNewRole('');
  };

  const handleRemoveRole = (role) => {
    onChange({
      settings: {
        ...settings,
        allowedRoles: allowedRoles.filter(r => r !== role)
      }
    });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('CompanyManagement.advanced_settings')}
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Alert severity="warning" sx={{ mb: 3 }}>
        {t('CompanyManagement.advanced_settings_warning')}
      </Alert>

      {/* Company Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <SecurityIcon color="primary" />
            <Typography variant="h6">
              {t('CompanyManagement.company_status')}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <FormControlLabel
            control={
              <Switch
                checked={company.active}
                onChange={(e) => handleActiveToggle(e.target.checked)}
                color="primary"
              />
            }
            label={t('CompanyManagement.company_active')}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {t('CompanyManagement.company_active_help')}
          </Typography>
        </CardContent>
      </Card>

      {/* Microsoft Integration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <MicrosoftIcon color="primary" />
            <Typography variant="h6">
              {t('CompanyManagement.microsoft_integration')}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Alert severity="info" sx={{ mb: 2 }}>
            {t('CompanyManagement.microsoft_integration_info')}
          </Alert>

          <TextField
            fullWidth
            label={t('CompanyManagement.microsoft_tenant_id')}
            value={microsoftTenantId}
            onChange={(e) => handleTenantIdChange(e.target.value)}
            placeholder={t('CompanyManagement.microsoft_tenant_id_placeholder')}
            helperText={t('CompanyManagement.microsoft_tenant_id_helper')}
          />
        </CardContent>
      </Card>

      {/* Allowed Domains */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('CompanyManagement.allowed_email_domains')}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Alert severity="info" sx={{ mb: 2 }}>
            {t('CompanyManagement.allowed_email_domains_info')}
          </Alert>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              label={t('CompanyManagement.domain')}
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder={t('CompanyManagement.domain_placeholder')}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddDomain();
                }
              }}
            />
            <IconButton
              color="primary"
              onClick={handleAddDomain}
              disabled={!newDomain}
            >
              <AddIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {allowedDomains.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                {t('CompanyManagement.no_domain_restrictions')}
              </Typography>
            ) : (
              allowedDomains.map((domain) => (
                <Chip
                  key={domain}
                  label={domain}
                  onDelete={() => handleRemoveDomain(domain)}
                  deleteIcon={<DeleteIcon />}
                />
              ))
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Allowed Roles */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('CompanyManagement.allowed_user_roles')}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Alert severity="info" sx={{ mb: 2 }}>
            {t('CompanyManagement.allowed_user_roles_info')}
          </Alert>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              label={t('CompanyManagement.role_name')}
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              placeholder={t('CompanyManagement.role_placeholder')}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddRole();
                }
              }}
            />
            <IconButton
              color="primary"
              onClick={handleAddRole}
              disabled={!newRole}
            >
              <AddIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {allowedRoles.map((role) => (
              <Chip
                key={role}
                label={role}
                color={role === 'admin' ? 'primary' : 'default'}
                onDelete={role !== 'admin' ? () => handleRemoveRole(role) : undefined}
                deleteIcon={<DeleteIcon />}
              />
            ))}
          </Box>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {t('CompanyManagement.admin_role_cannot_be_removed')}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdvancedTab;
